import React, { useState, useEffect, useContext, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Card, Button, Row, Col, Form, ButtonGroup } from "react-bootstrap";
import Select from "react-select";
import SbSmartSelect from "Components/SbSmartSelect/SbSmartSelect";
import { getPAICInferredJDs, getPAICProjects, createPAICProject, createPAICProjectRun, reinitiatePAICProjectRun } from "Api/apiService";
import { AuthContext } from "State/AuthContext";
import { buildFieldConfigMap } from "Utils/formatting";
import { PAICProjectContext } from "State/PAICProjectContext";
import SbInfoToaster from "Components/SbErrorToasterComponent/SbInfoToaster";
import SbInfoAlert from "Components/SbAlertComponent/SbInfoAlert";
import SbErrorAlert from "Components/SbAlertComponent/SbErrorAlert";
import SbWarningAlert from "Components/SbAlertComponent/SbWarningAlert";
import Permission from "Components/Permission";
import analytics from "Utils/analytics";
import "./SbPAC.scss";

const SEARCHABLE_FIELDS = [
    //'sb_requisition_number',
    //'job_title',
    'role_type',
    'legal_entity',
    'business_unit',
    'location',
    'function',
    'sub_function',
    'job_level',
    //'version'
];

const FILTER_KEYS = [
    //'sb_requisition_number',
    //'job_title',
    'role_type',
    'legal_entity',
    'business_unit',
    'location',
    'function',
    'sub_function',
    'job_level',
    //'version'
];
const INITIAL_FILTERS = Object.fromEntries(FILTER_KEYS.map(key => [key, []]));
const INITIAL_DROPDOWN_STATE = Object.fromEntries(FILTER_KEYS.map(key => [key, false]));
const INITIAL_SEARCH_INPUTS = Object.fromEntries(FILTER_KEYS.map(key => [key, '']));

const normalize = (val) => (val ?? '--').trim().toLowerCase();


const SbPeoplePlusAI = () => {
    const navigate = useNavigate();

    // Contexts
    const { email, refreshToken, orgMetaData } = useContext(AuthContext);
    const fieldMap = useMemo(() => buildFieldConfigMap(orgMetaData, 'req_no'), [orgMetaData]);
    const { setProjectId, loading, setLoading, setGroupName } = useContext(PAICProjectContext);

    // States
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [pageNumber, setPageNumber] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [error, setError] = useState(null);
    const [jobData, setJobData] = useState([]);
    const [projectGroupOptions, setProjectGroupOptions] = useState([]);
    const [paicProjects, setPAICProjects] = useState([]);

    const handleNextPage = () => {
        const newPage = Math.min(pageNumber + 1, totalPages);
        setPageNumber(newPage);
        
        // Track pagination
        analytics.trackPAICPaginationChange({
            from: 'people_plus_ai_main',
            email: email,
            page: newPage,
            itemsPerPage: itemsPerPage,
            description: `Page navigation: ${newPage}, Items: ${itemsPerPage}`
        });
    };
    
    const handlePreviousPage = () => {
        const newPage = Math.max(pageNumber - 1, 1);
        setPageNumber(newPage);
        
        // Track pagination
        analytics.trackPAICPaginationChange({
            from: 'people_plus_ai_main',
            email: email,
            page: newPage,
            itemsPerPage: itemsPerPage,
            description: `Page navigation: ${newPage}, Items: ${itemsPerPage}`
        });
    };

    // Filter states
    const [filters, setFilters] = useState(INITIAL_FILTERS);
    const [dropdownOpen, setDropdownOpen] = useState(INITIAL_DROPDOWN_STATE);
    const [searchInputs, setSearchInputs] = useState(INITIAL_SEARCH_INPUTS);

    // Search states
    const [pageSearchTerm, setPageSearchTerm] = useState('');
    const [searchOptions, setSearchOptions] = useState([]);

    const [selectedSearchOption, setSelectedSearchOption] = useState(null);
    const [selectedProjectGroup, setSelectedProjectGroup] = useState(null);
    const [newGroupName, setNewGroupName] = useState('');

    // Toaster state
    const [showToaster, setShowToaster] = useState(false);
    const [toasterMessage, setToasterMessage] = useState({ title: '', message: '', action: '', actionFunction: null, isReEvaluation: false });
    const [showEvaluatingAlert, setShowEvaluatingAlert] = useState(false);
    const [isReEvaluation, setIsReEvaluation] = useState(false);

    // Error Alert state
    const [showErrorAlert, setShowErrorAlert] = useState(false);
    const [errorAlertTitle, setErrorAlertTitle] = useState('');
    const [errorAlertMessage, setErrorAlertMessage] = useState('');

    // Warning Alert state
    const [showWarningAlert, setShowWarningAlert] = useState(false);

    const tableHeaders = [
        //{ key: 'sb_requisition_number', label: 'Position #' },
        { key: 'legal_entity', label: 'Legal Entity' },
        //{ key: 'job_title', label: 'Role Name' },
        { key: 'role_type', label: 'Role Type' },
        { key: 'business_unit', label: 'Business' },
        { key: 'location', label: 'Location' },
        { key: 'function', label: 'Function' },
        { key: 'sub_function', label: 'Sub-Function' },
        { key: 'job_level', label: 'Job Level' },
        //{ key: 'version_info', label: 'Version Info' },
    ];
    const anyDropdownOpen = Object.values(dropdownOpen).some(Boolean);

    // Custom Dropdown Indicator
    const DropdownIndicator = (props) => (
        <div {...props}>
            <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ marginRight: '8px' }}>
                <rect width="28" height="28" rx="4" fill="#008080" />
                <path d="M16.5013 15.8868C16.3667 15.7519 16.1839 15.6761 15.9934 15.6761H15.7904C15.7349 15.6761 15.6815 15.6546 15.6415 15.616C15.5597 15.5371 15.5541 15.4083 15.6245 15.3191C16.2755 14.4948 16.6627 13.4557 16.6627 12.3313C16.6627 9.66295 14.4997 7.5 11.8313 7.5C9.16295 7.5 7 9.66295 7 12.3313C7 14.9997 9.16295 17.1627 11.8313 17.1627C12.9557 17.1627 13.9948 16.7755 14.8191 16.1245C14.9083 16.0541 15.0371 16.0597 15.116 16.1415C15.1546 16.1815 15.1761 16.2349 15.1761 16.2904V16.4934C15.1761 16.6839 15.2519 16.8667 15.3868 17.0013L18.3382 19.9468C18.6444 20.2524 19.1403 20.2522 19.4463 19.9463C19.7522 19.6403 19.7524 19.1444 19.4468 18.8382L16.5013 15.8868ZM11.8313 15.6761C9.98056 15.6761 8.48656 14.1821 8.48656 12.3313C8.48656 10.4806 9.98056 8.98656 11.8313 8.98656C13.6821 8.98656 15.1761 10.4806 15.1761 12.3313C15.1761 14.1821 13.6821 15.6761 11.8313 15.6761Z" fill="white" />
            </svg>
        </div>
    );

    // APIs
    // Same as requisition view API but with additional filter for inference status
    const fetchInferredJobsRequisitionView = async () => {
        setLoading(true);
        setError(null);
        const payload = {
            email: email
        };
        try {
            const response = await getPAICInferredJDs(payload);
            setJobData(response);
            
            // Track refresh data
            analytics.trackPAICRefreshData({
                from: 'people_plus_ai_main',
                email: email,
                description: 'User refreshed PAIC data'
            });
        } catch (error) {
            console.log("error", error);
            setError(error);
        } finally {
            setLoading(false);
        }
    };

    // Fetch existing PAIC projects for organization
    const fetchPAICProjects = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await getPAICProjects();
            const allProjects = Array.isArray(response) ? response : [...(response?.owned_projects || []), ...(response?.delegated_projects || [])];
            const formattedOptions = allProjects.map(project => ({
                value: project.project_name,
                label: project.project_name,
                status: project.status
            }));
            setProjectGroupOptions(formattedOptions);
            setPAICProjects(allProjects);
        } catch (error) {
            console.log("error", error);
            setError(error);
        } finally {
            setLoading(false);
        }
    };

    // Create new PAIC project group
    const handleCreateGroup = async () => {
        const groupNameToCreate = newGroupName.trim();
        if (!groupNameToCreate) return;

        // Clear state immediately to prevent duplicate submissions on rapid Enter key presses
        const tempGroupName = groupNameToCreate;
        setNewGroupName('');
        setLoading(true);
        setError(null);
        
        let isDuplicateJobIds = false;
        
        try {
            const payload = {
                project_name: groupNameToCreate,
                job_ids: filteredJobs.map(job => job.sb_unique_id), // Sending filtered job IDs
                filters: filters,
            };
            await createPAICProject(payload);
            
            // Track group creation
            analytics.trackPAICCreateGroup({
                from: 'people_plus_ai_main',
                email: email,
                groupName: groupNameToCreate,
                rolesCount: filteredJobs.length,
                description: `Created group with ${filteredJobs.length} roles`
            });
            
            await fetchPAICProjects(); // Refresh options
        } catch (error) {
            console.error("Error creating group:", error);
            
            // Handle specific error codes
            const errorCode = error?.response?.data?.error_code;
            const errorMessage = error?.response?.data?.message;
            const projectId = error?.response?.data?.sb_project_unique_id;
            
            if (errorCode === 7202) {
                // Error: Project with same name already exists
                setErrorAlertTitle('Duplicate Project Name');
                setErrorAlertMessage(`${tempGroupName} already exists, please give a different name`);
                setShowErrorAlert(true);
                
                // Reset the input field and hide alert after 3 seconds
                setTimeout(() => {
                    setShowErrorAlert(false);
                }, 3000);
                
                // Restore the group name so user can edit it
                setNewGroupName(tempGroupName);
            } else if (errorCode === 7204) {
                // Error: Project with same job IDs already exists
                isDuplicateJobIds = true;
                
                // Show warning alert first
                setShowWarningAlert(true);
                
                // After 3 seconds, hide alert and populate the group
                setTimeout(async () => {
                    setShowWarningAlert(false);
                    
                    // Clear filters to switch back to dropdown view
                    setFilters(INITIAL_FILTERS);
                    
                    // Refresh to get the existing project
                    const response = await getPAICProjects();
                    const allProjects = Array.isArray(response) ? response : [...(response?.owned_projects || []), ...(response?.delegated_projects || [])];
                    const formattedOptions = allProjects.map(project => ({
                        value: project.project_name,
                        label: project.project_name,
                        status: project.status
                    }));
                    setProjectGroupOptions(formattedOptions);
                    setPAICProjects(allProjects);
                    
                    // Find and select the existing project by ID from the fresh response
                    const existingProject = allProjects.find(p => p.sb_project_unique_id === projectId);
                    if (existingProject) {
                        setSelectedProjectGroup(existingProject.project_name);
                    }
                    
                    // Finally set loading to false after everything is done
                    setLoading(false);
                }, 3000);
            } else {
                // Generic error handling
                setError(error);
                setNewGroupName(tempGroupName);
            }
        } finally {
            // Only set loading to false if it's not the duplicate job IDs case
            if (!isDuplicateJobIds) {
                setLoading(false);
            }
        }
    };

    // Handle how Evaluate/View PAIC potential button works
    const handlePAICPotentialClick = async () => {
        if (!selectedProjectGroup) return;

        const selectedProject = paicProjects.find(p => p.project_name === selectedProjectGroup);
        if (!selectedProject || !selectedProject.sb_project_unique_id) {
            console.error("Selected project or ID not found");
            return;
        }

        // If Completed, navigate to agents with selected project ID
        if (selectedProject.status === 'Completed') {
            // Track view potential
            analytics.trackPAICViewPotential({
                from: 'people_plus_ai_main',
                email: email,
                groupName: selectedProject.project_name,
                description: 'Viewing completed PAIC potential'
            });
            
            setProjectId(selectedProject.sb_project_unique_id);
            setGroupName(selectedProject.project_name);
            navigate('/people-plus-ai/pac-group-agents');
            return;
        }

        // If Processing, do nothing (should be disabled anyway)
        if (selectedProject.status === 'Processing') {
            return;
        }

        // If Failed, show toaster and if proceed, reinitiate project run
        if (selectedProject.status === 'Failed') {
            setIsReEvaluation(true);
            setToasterMessage({
                isReEvaluation: true,
                actionFunction: async () => {
                    setShowToaster(false);
                    setLoading(true);
                    setError(null);
                    
                    // Track re-evaluate initiation
                    analytics.trackPAICReEvaluateInitiated({
                        from: 'people_plus_ai_main',
                        email: email,
                        groupName: selectedProject.project_name,
                        description: 'Initiated PAIC re-evaluation'
                    });
                    
                    try {
                        const payload = {
                            sb_project_unique_id: selectedProject.sb_project_unique_id,
                        };
                        const response = await reinitiatePAICProjectRun(payload, refreshToken);
                        if (response.status === 'Processing') {
                            // Proceed with re-initiating the project
                            setShowEvaluatingAlert(true);

                            setTimeout(() => {
                                setShowEvaluatingAlert(false);
                                setIsReEvaluation(false);
                            }, 3000);
                        }
                    } catch (error) {
                        console.error("Error reinitiating project run:", error);
                        setError(error);
                    } finally {
                        fetchPAICProjects();
                        setLoading(false);
                    }
                }
            });
            setShowToaster(true);
            return;
        }

        // If Created, show toaster and if proceed, create project run, initiate group PAIC and poll for status
        if (selectedProject.status === 'Created') {
            setIsReEvaluation(false);
            setToasterMessage({
                isReEvaluation: false,
                actionFunction: async () => {
                    setShowToaster(false);
                    setLoading(true);
                    setError(null);
                    
                    // Track evaluate initiation
                    analytics.trackPAICEvaluateInitiated({
                        from: 'people_plus_ai_main',
                        email: email,
                        groupName: selectedProject.project_name,
                        description: 'Initiated PAIC evaluation'
                    });
                    
                    try {
                        const payload = {
                            sb_project_unique_id: selectedProject.sb_project_unique_id,
                        };
                        const response = await createPAICProjectRun(payload, refreshToken);
                        if (response.status === 'Processing') {
                            // Proceed with initiating the project
                            setShowEvaluatingAlert(true);

                            setTimeout(() => {
                                setShowEvaluatingAlert(false);
                                setIsReEvaluation(false);
                            }, 3000);
                        }
                    } catch (error) {
                        console.error("Error creating project run:", error);
                        setError(error);
                    } finally {
                        fetchPAICProjects();
                        setLoading(false);
                    }
                }
            });
            setShowToaster(true);
            return;
        }
    };


    // Effects
    useEffect(() => {
        fetchInferredJobsRequisitionView();
        fetchPAICProjects();
    }, []);

    // Close dropdowns on click outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (Object.values(dropdownOpen).some(Boolean) && !event.target.closest('.filter-header-cell')) {
                setDropdownOpen(INITIAL_DROPDOWN_STATE);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [dropdownOpen]);

    // Helper to compare filter objects
    const areFiltersEqual = (filters1, filters2) => {
        if (!filters1 || !filters2) return false;
        const keys1 = Object.keys(filters1);
        const keys2 = Object.keys(filters2);

        // Combine all unique keys
        const allKeys = new Set([...keys1, ...keys2]);

        for (let key of allKeys) {
            // Ensure we have arrays, handle null/undefined cases
            const val1 = Array.isArray(filters1[key]) ? filters1[key] : [];
            const val2 = Array.isArray(filters2[key]) ? filters2[key] : [];

            if (val1.length !== val2.length) return false;

            const sorted1 = [...val1].map(normalize).sort();
            const sorted2 = [...val2].map(normalize).sort();

            if (sorted1.some((v, i) => v !== sorted2[i])) return false;
        }
        return true;
    };

    // Memoize active filter count
    const activeFilterCount = Object.values(filters).reduce((acc, curr) => {
        return acc + (Array.isArray(curr) ? curr.length : 0);
    }, 0);

    // Memoize matching project for use in render and effect
    const matchingProject = useMemo(() => {
        if (!paicProjects || paicProjects.length === 0 || activeFilterCount === 0) return null;
        return paicProjects.find(project => project.filters && areFiltersEqual(filters, project.filters));
    }, [filters, paicProjects, activeFilterCount]);

    // Auto-populate group dropdown if filters match
    useEffect(() => {
        if (matchingProject) {
            // Avoid redundant updates
            if (selectedProjectGroup !== matchingProject.project_name) {
                setSelectedProjectGroup(matchingProject.project_name);
            }
        } else {
            // If filters are active but don't match any group, clear selection
            if (activeFilterCount > 0 && selectedProjectGroup !== null) {
                setSelectedProjectGroup(null);
            }
        }
    }, [matchingProject, activeFilterCount]);


    // Handlers
    const handleSearchInputChange = (key, value) => {
        setSearchInputs(prev => ({ ...prev, [key]: value }));
    };

    const getUniqueValues = (key) => {
        return [...new Set(jobData?.map(job => normalize(job[key])))].map(val => {
            const original = jobData?.find(j => normalize(j[key]) === val);
            return original?.[key] ?? '--';
        });
    };

    const filteredDropdownValues = (key) => {
        const allValues = getUniqueValues(key);
        const searchTerm = searchInputs[key].toLowerCase();
        return allValues.filter(value => normalize(value).includes(searchTerm));
    };

    const toggleDropdown = (key) => {
        setDropdownOpen(prev => ({
            ...Object.fromEntries(Object.keys(prev)?.map(k => [k, false])),
            [key]: !prev[key]
        }));
    };

    const handleFilterChange = (key, value) => {
        setFilters(prev => {
            const selected = prev[key];
            const updated = selected.includes(value) ? selected.filter(v => v !== value) : [...selected, value];
            
            // Track filter change
            analytics.trackPAICFilterRoles({
                from: 'people_plus_ai_main',
                email: email,
                filterType: key,
                filterValue: value,
                description: `Applied filter: ${key}=${value}`
            });
            
            return { ...prev, [key]: updated };
        });
    };

    const handleSelectAll = (key) => {
        const all = getUniqueValues(key);
        setFilters(prev => {
            const currentFilters = Array.isArray(prev[key]) ? prev[key] : [];
            return {
                ...prev,
                [key]: currentFilters.length === all.length ? [] : all
            };
        });
    };

    const handlePageNumberChange = (value) => {
        const numValue = parseInt(value) || 1;
        const clampedValue = Math.max(1, Math.min(numValue, totalPages));
        setPageNumber(clampedValue);
    };

    const isFilterActive = (key) => Array.isArray(filters[key]) && filters[key].length > 0;

    const clearAllFilters = () => {
        setFilters(INITIAL_FILTERS);
        setDropdownOpen(INITIAL_DROPDOWN_STATE);
        setSearchInputs(INITIAL_SEARCH_INPUTS);
        setPageSearchTerm('');

        setSelectedSearchOption(null);
        setSelectedProjectGroup(null);
        
        // Track clear filters
        analytics.trackPAICClearFilters({
            from: 'people_plus_ai_main',
            email: email,
            description: 'Cleared all role filters'
        });
    };


    const filteredJobs = useMemo(() => {
        // Primary filter: only jobs with GENERATED inference status
        const primaryFilteredJobs = jobData?.filter(job =>
            job.skills_inference_status === "GENERATED" && job.tasks_inference_status === "GENERATED"
        );

        // Apply page search
        const searchTerm = normalize(pageSearchTerm);
        const pageSearchedJobs = primaryFilteredJobs?.filter(job => {
            if (!searchTerm) return true;
            return SEARCHABLE_FIELDS.some(field =>
                job[field] && typeof job[field] === 'string' && normalize(job[field]).includes(searchTerm)
            );
        });

        // Apply column filters
        let columnFilteredJobs = pageSearchedJobs?.filter(job =>
            FILTER_KEYS.every(key => {
                const filterValues = filters[key];
                // Check if filterValues is an array and has length
                if (!Array.isArray(filterValues) || filterValues.length === 0) return true;
                return filterValues.map(normalize).includes(normalize(job[key]));
            })
        );

        // Apply status filters
        // if (criticalStatusFilter === 'critical') {
        //   columnFilteredJobs = columnFilteredJobs.filter(
        //     job => job?.critical_role === true || job?.critical_role === "true"
        //   );
        // } else if (criticalStatusFilter === 'nonCritical') {
        //   columnFilteredJobs = columnFilteredJobs.filter(
        //     job => !job?.critical_role || job?.critical_role === false || job?.critical_role === "false"
        //   );
        // }

        // if (jdStatusFilter === 'published') {
        //   columnFilteredJobs = columnFilteredJobs?.filter(job => job?.jd_status === 'PUBLISHED');
        // } else if (jdStatusFilter === 'unpublished') {
        //   columnFilteredJobs = columnFilteredJobs?.filter(job => job?.jd_status !== 'PUBLISHED');
        // }
        // const filterJobIdProgress = columnFilteredJobs?.filter(job => job?.jd_status === 'PENDING_ANALYSIS' && job?.user_id === email);

        // if (filterJobIdProgress.length > 0 && jobTaskId === "" && messages === "") {
        //   setReviseJdProgress(true);
        //   setJobTaskId({ task_id: filterJobIdProgress[0].sb_unique_id, job_id: filterJobIdProgress[0].sb_unique_id })
        // }
        // if (filterJobIdProgress.length === 0) {
        //   setReviseJdProgress(false);
        // }

        // Apply Project Group Filter
        if (selectedProjectGroup) {
            const selectedProject = paicProjects.find(p => p.project_name === selectedProjectGroup);
            if (selectedProject && selectedProject.filters) {
                const storedFilters = selectedProject.filters;
                columnFilteredJobs = columnFilteredJobs?.filter(job =>
                    Object.keys(storedFilters).every(key => {
                        const filterValues = storedFilters[key];
                        // Check if filterValues is an array and has length
                        if (!Array.isArray(filterValues) || filterValues.length === 0) return true;
                        return filterValues.map(normalize).includes(normalize(job[key]));
                    })
                );
            }
        }


        return columnFilteredJobs;
    }, [jobData, pageSearchTerm, filters, email, selectedProjectGroup, paicProjects]);

    // Update totalPages when filteredJobs or itemsPerPage changes
    useEffect(() => {
        const newTotalPages = Math.ceil(filteredJobs.length / itemsPerPage) || 1;
        setTotalPages(newTotalPages);
        // Reset page to 1 if current page exceeds new total pages
        if (pageNumber > newTotalPages) {
            setPageNumber(1);
        }
    }, [filteredJobs.length, itemsPerPage, pageNumber]);

    // Reset page to 1 when filters change
    useEffect(() => {
        setPageNumber(1);
    }, [filters, pageSearchTerm, selectedProjectGroup]);

    return (
        <>
            <div className="jobs-library-container shadow-sm">

                {/* Fixed Header */}
                <div className="jobs-library-header">
                    <div className="d-flex align-items-center justify-content-between">
                        <Card.Body>
                            <Card.Title className="text-start jobs-heading">
                                <div className="d-flex align-items-center gap-2">
                                    <h2>
                                        People+AI Collaboration (PAIC)
                                    </h2>
                                    <button
                                        onClick={fetchInferredJobsRequisitionView}
                                        className="refresh-btn"
                                        title="Refresh Data"
                                    >
                                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path fillRule="evenodd" clipRule="evenodd" d="M10 5C10 5.55 10.45 6 11 6H15C15.55 6 16 5.55 16 5V1C16 0.45 15.55 0 15 0C14.45 0 14 0.45 14 1V2.74C12.54 1.07 10.4 0 8 0C3.58 0 0 3.58 0 8C0 12.06 3.02 15.4 6.94 15.92C6.96 15.92 6.98 15.93 7 15.93C7.33 15.97 7.66 16 8 16C12.42 16 16 12.42 16 8C16 7.45 15.55 7 15 7C14.45 7 14 7.45 14 8C14 11.31 11.31 14 8 14C7.29 14 6.63 13.85 6 13.62V13.63C3.67 12.81 2 10.61 2 8C2 4.69 4.69 2 8 2C9.77 2 11.36 2.78 12.46 4H11C10.45 4 10 4.45 10 5Z" fill="white" />
                                        </svg>
                                    </button>
                                </div>
                            </Card.Title>
                            <Card.Text className="text-start desc-text mt-2">
                                <span className='text-body-tertiary jobs-description text-start'>
                                    This module helps you identify, prioritize, and assign AI agents to collaborate with your workforce.
                                    <br />
                                    1. Select the roles you want AI agents to work with. Name this group.
                                    <br />
                                    2. Click “Evaluate PAIC Potential” to continue.

                                </span>
                            </Card.Text>
                        </Card.Body>

                        <Card.Body className="d-flex align-items-end justify-content-end flex-column">
                            {/* Search Component */}
                            <div className="search-wrapper w-500 mb-2 me-2">
                                <Select
                                    className="jobs-library-select"
                                    classNamePrefix="jobs-library-select"
                                    // options={filteredJobs.map(job => ({
                                    //     value: job.sb_unique_id,
                                    //     label: job.job_title
                                    // }))}
                                    // value={selectedSearchOption}
                                    // onChange={(option) => {
                                    //     setSelectedSearchOption(option);
                                    //     if (option) setPageSearchTerm(option.label);
                                    // }}
                                    // onInputChange={(inputValue) => setPageSearchTerm(inputValue)}
                                    placeholder="Search role by keyword, skill, etc."
                                    isClearable
                                    isSearchable
                                    isDisabled={true}
                                    components={{
                                        DropdownIndicator,
                                        IndicatorSeparator: () => null
                                    }}
                                    // noOptionsMessage={({ inputValue }) => {
                                    //     // Track no results searches
                                    //     if (inputValue.length > 0) {
                                    //         trackSearch({
                                    //             searchTerm: inputValue,
                                    //             searchMethod: 'typed',
                                    //             resultCount: 0,
                                    //             duration: searchStartTime ? Date.now() - searchStartTime : null,
                                    //             selectedOption: null
                                    //         });
                                    //     }
                                    //     return "No matching results";
                                    // }}
                                    maxMenuHeight={200}
                                />
                            </div>
                            <div className="d-flex align-items-center gap-4">
                                <div style={{ width: '360px', alignItems: 'center' }}>
                                    {activeFilterCount > 0 && !matchingProject ? (
                                        <Permission resource="people_plus_ai" actions={["create"]}>
                                            <div className="d-flex gap-2">
                                                <div className="text-primary text-nowrap small " style={{ height: '38px', display: 'flex', alignItems: 'center' }}>Create New Group:</div>
                                                <Form.Control
                                                    size="sm"
                                                    type="text"
                                                    placeholder="Give it a name and click enter"
                                                    value={newGroupName}
                                                    onChange={(e) => setNewGroupName(e.target.value)}
                                                    onKeyDown={(e) => {
                                                        if (e.key === 'Enter') {
                                                            e.preventDefault();
                                                            handleCreateGroup();
                                                        }
                                                    }}
                                                />
                                            </div>
                                        </Permission>
                                    ) : (
                                        <SbSmartSelect
                                            options={projectGroupOptions}
                                            value={selectedProjectGroup}
                                            onChange={(value) => {
                                                if (!value) {
                                                    clearAllFilters();
                                                } else {
                                                    // Clear existing filters first
                                                    setFilters(INITIAL_FILTERS);
                                                    setDropdownOpen(INITIAL_DROPDOWN_STATE);
                                                    setSearchInputs(INITIAL_SEARCH_INPUTS);
                                                    
                                                    // Then set the new group
                                                    setSelectedProjectGroup(value);
                                                    
                                                    // Apply the new group's filters
                                                    const selectedProject = paicProjects.find(p => p.project_name === value);
                                                    if (selectedProject && selectedProject.filters) {
                                                        setFilters(selectedProject.filters);
                                                    }
                                                    
                                                    // Track group selection
                                                    analytics.trackPAICSelectGroup({
                                                        from: 'people_plus_ai_main',
                                                        email: email,
                                                        groupName: value,
                                                        description: `Selected group: ${value}`
                                                    });
                                                }
                                            }}
                                            placeholder="Select or create group"
                                            isSearchable
                                            isClearable
                                            autoSelectSingleOption={false}
                                        />
                                    )}
                                </div>

                                {(() => {
                                    const p = paicProjects.find(proj => proj.project_name === selectedProjectGroup);
                                    const isProcessing = p?.status === 'Processing';
                                    const isCompleted = p?.status === 'Completed';
                                    const isFailed = p?.status === 'Failed';
                                    const isCreated = p?.status === 'Created';

                                    // View PAIC Potential - requires 'view' permission
                                    if (isCompleted) {
                                        return (
                                            <Permission resource="people_plus_ai" actions={["view"]}>
                                                <Button
                                                    variant="primary"
                                                    onClick={handlePAICPotentialClick}
                                                    style={{ width: '220px', fontSize: '14px' }}
                                                    disabled={loading || !selectedProjectGroup}
                                                >
                                                    View PAIC Potential
                                                </Button>
                                            </Permission>
                                        );
                                    }

                                    // Processing state - no permission required, button is disabled
                                    if (isProcessing) {
                                        return (
                                            <Button
                                                variant="primary"
                                                onClick={handlePAICPotentialClick}
                                                style={{ width: '220px', fontSize: '14px' }}
                                                disabled={true}
                                            >
                                                Processing PAIC Potential
                                            </Button>
                                        );
                                    }

                                    // Evaluate or Re-Evaluate - requires 'create' permission
                                    // This covers Created, Failed, or no selection
                                    return (
                                        <Permission resource="people_plus_ai" actions={["create"]}>
                                            <Button
                                                variant="primary"
                                                onClick={handlePAICPotentialClick}
                                                style={{ width: '220px', fontSize: '14px' }}
                                                disabled={loading || !selectedProjectGroup}
                                            >
                                                {isFailed ? 'Re-Evaluate PAIC Potential' : 'Evaluate PAIC Potential'}
                                            </Button>
                                        </Permission>
                                    );
                                })()}

                            </div>
                        </Card.Body>
                    </div>
                </div>

                {/* Scrollable Table */}
                <div className="jobs-library-container shadow-sm">
                    <div className="pac-table-scroll-wrapper">
                        <table hover className="table table-hover">
                            <thead className="sticky-table-header">
                                <tr className='text-body-tertiary text-start text-uppercase jobs-table-heading'>
                                    <th>{fieldMap['job_code']?.label || "Role Id"}</th>
                                    <th>Legal Entity</th>
                                    <th>Role Name</th>
                                    {tableHeaders.filter(h => h.key !== 'legal_entity').map((header) => (
                                        <th key={header.key} className={`filter-header-cell ${anyDropdownOpen && !dropdownOpen[header.key] ? 'dimmed-column' : ''}`} style={{ position: 'relative' }}>
                                            <span
                                                style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}
                                                onClick={() => toggleDropdown(header.key)}
                                                className={`filter-toggle-icon ${isFilterActive(header.key) ? 'active' : ''}`}
                                            >
                                                {header.label}
                                                <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg" className="ms-2">
                                                    <path fillRule="evenodd" clipRule="evenodd" d="M13 0H1C0.45 0 0 0.45 0 1C0 1.28 0.11 1.53 0.29 1.71L5 6.42V13.01C5 13.56 5.45 14.01 6 14.01C6.28 14.01 6.53 13.9 6.71 13.72L8.71 11.72C8.89 11.54 9 11.29 9 11.01V6.42L13.71 1.71C13.89 1.53 14 1.28 14 1C14 0.45 13.55 0 13 0Z"
                                                        fill={isFilterActive(header.key) ? "#008080" : "#8F95B2"}
                                                    />
                                                </svg>
                                            </span>
                                            {dropdownOpen[header.key] && (
                                                <div
                                                    className="filter-dropdown"
                                                    onClick={(e) => e.stopPropagation()}
                                                >
                                                    <input
                                                        type="text"
                                                        className="form-control mb-2"
                                                        placeholder="Search..."
                                                        value={searchInputs[header.key]}
                                                        onChange={(e) => handleSearchInputChange(header.key, e.target.value)}
                                                    />
                                                    <label className="d-flex align-items-center">
                                                        <input
                                                            type="checkbox"
                                                            className="form-check-input"
                                                            checked={Array.isArray(filters[header.key]) && filters[header.key].length === getUniqueValues(header.key).length}
                                                            onChange={() => handleSelectAll(header.key)}
                                                        />
                                                        Select All
                                                    </label>
                                                    {filteredDropdownValues(header.key)?.map((value, idx) => (
                                                        <label key={idx} className="d-flex align-items-center">
                                                            <input
                                                                type="checkbox"
                                                                className="form-check-input"
                                                                checked={Array.isArray(filters[header.key]) && filters[header.key].includes(value)}
                                                                onChange={() => handleFilterChange(header.key, value)}
                                                            />
                                                            {value}
                                                        </label>
                                                    ))}
                                                </div>
                                            )}
                                        </th>
                                    ))}
                                    <th>Version</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading ? (
                                    <tr>
                                        <td colSpan={11} className="text-center">
                                            <div className="d-flex justify-content-center my-3">
                                                <div className="spinner-border" role="status"></div>
                                            </div>
                                        </td>
                                    </tr>
                                ) : filteredJobs.length === 0 && selectedProjectGroup ? (
                                    <tr>
                                        <td colSpan={11} className="text-center py-4">
                                            <div className="text-muted">
                                                The selected group, <strong>{selectedProjectGroup}</strong> has no matching job descriptions.
                                            </div>
                                        </td>
                                    </tr>
                                ) : filteredJobs.length === 0 ? (
                                    <tr>
                                        <td colSpan={11} className="text-center py-4">
                                            <div className="text-muted">
                                                No job descriptions available for applied filters.
                                            </div>
                                        </td>
                                    </tr>
                                ) : (
                                    filteredJobs
                                        .slice((pageNumber - 1) * itemsPerPage, pageNumber * itemsPerPage)
                                        .map((job, index) => (
                                            <tr key={index} className="jobs-table-content">
                                                <td>{job.sb_requisition_number}</td>
                                                <td>{job.legal_entity}</td>
                                                <td>{job.job_title}</td>
                                                <td>{job.role_type}</td>
                                                <td>{job.business_unit}</td>
                                                <td>{job.location}</td>
                                                <td>{job.function}</td>
                                                <td>{job.sub_function}</td>
                                                <td>{job.job_level}</td>
                                                <td>{job.version}</td>
                                            </tr>
                                        ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* Fixed Footer */}
                <div className="jobs-library-footer">
                    <Row className="align-items-center w-100">
                        <Col xs={4} className="d-flex align-items-center justify-content-start">
                            <Row className="align-items-center g-2 flex-nowrap">
                                <Col
                                    xs="auto"
                                    onClick={clearAllFilters}
                                    title="Clear All Filters"
                                    style={{ cursor: 'pointer' }}
                                >
                                    <svg className="me-2" width="25" height="25" viewBox="0 0 16 14" fill="none">
                                        <path
                                            fillRule="evenodd"
                                            clipRule="evenodd"
                                            d="M12 1C12 0.45 11.55 0 11 0H1C0.45 0 0 0.45 0 1C0 1.28 0.11 1.53 0.29 1.71L4 5.41V11C4 11.55 4.45 12 5 12C5.28 12 5.53 11.89 5.71 11.71L7.71 9.71C7.89 9.53 8 9.28 8 9V5.41L11.71 1.7C11.89 1.53 12 1.28 12 1ZM14.41 11L15.7 9.71C15.89 9.53 16 9.28 16 9C16 8.45 15.55 8 15 8C14.72 8 14.47 8.11 14.29 8.29L13 9.59L11.71 8.3C11.53 8.11 11.28 8 11 8C10.45 8 10 8.45 10 9C10 9.28 10.11 9.53 10.29 9.71L11.59 11L10.3 12.29C10.11 12.47 10 12.72 10 13C10 13.55 10.45 14 11 14C11.28 14 11.53 13.89 11.71 13.71L13 12.41L14.29 13.7C14.47 13.89 14.72 14 15 14C15.55 14 16 13.55 16 13C16 12.72 15.89 12.47 15.71 12.29L14.41 11Z"
                                            fill={Object.values(filters).some(arr => arr.length > 0) ? '#008080' : '#8F95B2'}
                                        />
                                    </svg>
                                </Col>
                                <Col xs="auto">
                                    {Object.values(filters).some(arr => arr.length > 0) ? (
                                        <div className="text-primary">
                                            {filteredJobs.length} Roles selected
                                        </div>
                                    ) : (
                                        <div className="text-body-tertiary">
                                            0 Roles selected. Please select at least one role
                                        </div>
                                    )}
                                </Col>
                            </Row>
                        </Col>

                        <Col xs={4} className="d-flex align-items-center justify-content-center">
                            <Row className="align-items-center g-2">
                                <Col xs="auto">Showing</Col>
                                <Col xs="auto">
                                    <Form.Select
                                        size="sm"
                                        className="bg-light"
                                        value={itemsPerPage}
                                        onChange={(e) => {
                                            const newItemsPerPage = parseInt(e.target.value);
                                            setItemsPerPage(newItemsPerPage);
                                            setPageNumber(1);
                                            
                                            // Track pagination change
                                            analytics.trackPAICPaginationChange({
                                                from: 'people_plus_ai_main',
                                                email: email,
                                                page: 1,
                                                itemsPerPage: newItemsPerPage,
                                                description: `Changed items per page to ${newItemsPerPage}`
                                            });
                                        }}
                                    >
                                        <option value="10">10</option>
                                        <option value="20">20</option>
                                        <option value="30">30</option>
                                    </Form.Select>
                                </Col>
                                <Col xs="auto">Items per page</Col>
                            </Row>
                        </Col>

                        <Col xs={4} className="d-flex align-items-center justify-content-end">
                            <Row className="align-items-center g-2">
                                <Col xs="auto">Page</Col>
                                <Col xs="auto">
                                    <Form.Control
                                        size="sm"
                                        type="tel"
                                        value={pageNumber}
                                        onChange={(e) => handlePageNumberChange(e.target.value)}
                                        className="bg-light page-number-input"
                                    />
                                </Col>
                                <Col xs="auto">of {totalPages}</Col>
                                <Col xs="auto">
                                    <ButtonGroup>
                                        <Button
                                            variant="secondary"
                                            onClick={handlePreviousPage}
                                            disabled={pageNumber === 1}
                                        >
                                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                                                <g opacity="0.9">
                                                    <path
                                                        d="M15.41 16.4064L10.83 12L15.41 7.59359L14 6.23999L8 12L14 17.76L15.41 16.4064Z"
                                                        fill="#565E70"
                                                    />
                                                </g>
                                            </svg>
                                        </Button>
                                        <Button
                                            variant="secondary"
                                            onClick={handleNextPage}
                                            disabled={pageNumber === totalPages}
                                        >
                                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                                                <g opacity="0.9">
                                                    <path
                                                        d="M8.59 16.4064L13.17 12L8.59 7.59359L10 6.23999L16 12L10 17.76L8.59 16.4064Z"
                                                        fill="#565E70"
                                                    />
                                                </g>
                                            </svg>
                                        </Button>
                                    </ButtonGroup>
                                </Col>
                            </Row>
                        </Col>
                    </Row>
                </div>
            </div>
            
            {/* Confirmation Toaster */}
            <SbInfoToaster
                show={showToaster}
                onClose={() => setShowToaster(false)}
                title={toasterMessage.isReEvaluation ? `Re-Evaluate ${selectedProjectGroup}?` : `Evaluate ${selectedProjectGroup}?`}
                message={toasterMessage.isReEvaluation 
                    ? `This will re-evaluate the group <strong>${selectedProjectGroup}</strong> and may take up to a few hours - a notification will appear once it is done.`
                    : `This will evaluate the group <strong>${selectedProjectGroup}</strong> and may take up to a few hours - a notification will appear once it is done.`
                }
                action="Proceed"
                actionFunction={toasterMessage.actionFunction}
                viewCancel={true}
                disabled={loading}
            />
            {/* Evaluating Alert */}
            <SbInfoAlert
                show={showEvaluatingAlert}
                title={isReEvaluation ? "PAIC Re-Evaluation Initiated" : "PAIC Evaluation Initiated"}
                message={isReEvaluation 
                    ? `The re-evaluation for the group <strong>${selectedProjectGroup}</strong> has been initiated. You will be notified once the process is complete.`
                    : `The evaluation for the group <strong>${selectedProjectGroup}</strong> has been initiated. You will be notified once the process is complete.`
                }
            />
            {/* Error Alert */}
            <SbErrorAlert
                show={showErrorAlert}
                title={errorAlertTitle}
                message={errorAlertMessage}
                onClose={() => setShowErrorAlert(false)}
                duration={3000}
            />
            {/* Warning Alert */}
            <SbWarningAlert
                show={showWarningAlert}
                title="Duplicate Job Selection"
                message="A group already exists with the selected JDs"
                setShow={setShowWarningAlert}
            />
        </>
    );
}
export default SbPeoplePlusAI;